<template>
  <PopupInputgroup
    ref="popup"
    class="popup-ref-num-value-edit"
    :label="label"
    :append-to="appendTo"
    :disabled="!!disabled"
    :invalid="v$.$invalid"
    hide-edit-icon
    @cancel="onCancel"
    @focus="onFocus"
    @save="onAutoSave"
  >
    <template #popupContent>
      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        :input-id="id"
        :aria-label="label"
        :errors="v$.internalValue.$errors"
        :help-text="clientReferenceNumberType?.referenceNumberType?.description"
      >
        <RygenDropdown
          v-if="
            clientReferenceNumberType?.referenceNumberType?.valueType ===
            ReferenceNumberValueType.DROPDOWN
          "
          :id="inputId"
          ref="input"
          v-model="internalValue.value"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :editable="
            clientReferenceNumberType.referenceNumberType.allowFreeformValue
          "
          :options="clientReferenceNumberType.referenceNumberType.valueOptions"
          option-label="label"
          option-value="value"
          filter
        />
        <RygenCalendar
          v-else-if="isDateOrTimeType"
          :id="inputId"
          ref="input"
          v-model="internalValue.valueAsDateTime"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :show-time="isTimeType"
          :time-only="isTimeTypeOnly"
          hour-format="12"
        />
        <InputNumber
          v-else-if="
            clientReferenceNumberType?.referenceNumberType?.valueType ===
            ReferenceNumberValueType.NUMERIC
          "
          ref="input"
          v-model.trim="internalValue.valueAsNumber"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :input-id="inputId"
          @keyup.enter="onSave"
          @keyup.esc="closePopup"
          @keydown.tab.passive="onSave"
        />
        <span
          v-else-if="isFreeformMultiChoice"
          class="flex flex-column flex-gap-1"
        >
          <small :id="`${id}-help`" class="input-wrapper-help font-light">
            {{ t('referenceData.freeformMultichoiceHelpColumn') }}
          </small>

          <RygenMultiSelect
            v-bind="ariaProps"
            :id="inputId"
            ref="input"
            v-model="internalValue.valueAsArray"
            :class="inputClasses"
            :invalid="invalid"
            :disabled="!!disabled"
            :options="
              clientReferenceNumberType?.referenceNumberType?.valueOptions
            "
            option-label="label"
            option-value="value"
            filter
          />
          <InputText
            :id="`${id}-freeform`"
            v-model.trim="internalValue.value"
            v-bind="ariaProps"
            :class="inputClasses"
            :invalid="invalid"
            :disabled="!!disabled"
            @keyup.enter="onSave"
            @keyup.esc="closePopup"
            @keydown.tab.passive="onSave"
          />
        </span>
        <RygenMultiSelect
          v-else-if="isMultiChoice"
          v-bind="ariaProps"
          :id="inputId"
          ref="input"
          v-model="internalValue.valueAsArray"
          :class="inputClasses"
          :invalid="invalid"
          :options="
            clientReferenceNumberType?.referenceNumberType?.valueOptions
          "
          option-label="label"
          option-value="value"
          filter
        />
        <InputText
          v-else
          :id="inputId"
          ref="input"
          v-model.trim="internalValue.value"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :disabled="!!disabled"
          @keyup.enter="onSave"
          @keyup.esc="closePopup"
          @keydown.tab.passive="onSave"
        />
      </InputWrapper>
    </template>

    <template #popupButtons="{ invalid }">
      <RygenButton
        mode="tertiary"
        :label="t('common.cancel')"
        tabindex="-1"
        @click.stop="closePopup"
      />
      <RygenButton
        v-if="$hasAuthority('referenceData', 'delete') && editable"
        mode="secondary"
        :severity="Severity.DANGER"
        :label="t('common.delete')"
        tabindex="-1"
        @click.stop="onDelete"
      />
      <RygenButton
        mode="primary"
        :disabled="invalid"
        :label="t('common.done')"
        tabindex="-1"
        @click.stop="onSave"
      />
    </template>

    <template #placeholder="{ label: placeholderLabel }">
      <slot
        name="placeholder"
        :label="placeholderLabel"
        :placeholder="placeholderValue"
      >
        <KeyValuePair
          :class="{ 'no-label': !label }"
          :label="label"
          :value="placeholderValue"
          inline
        />
      </slot>
    </template>
  </PopupInputgroup>
</template>

<script lang="ts" setup>
import {
  ComponentPublicInstance,
  computed,
  nextTick,
  PropType,
  Ref,
  ref,
  toRefs,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import { useUid, useValidations } from '@rygen/rygen-ui/composables';
import {
  ClientReferenceNumberType,
  ReferenceNumberValueType,
  Severity,
} from '@rygen/rygen-ui/model';

import { useReferenceNumberValueTypes } from '@/composables';
import { ReferenceNumber } from '@/model';

import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenCalendar from '@rygen/rygen-ui/components/form/RygenCalendar.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';
import RygenMultiSelect from '@rygen/rygen-ui/components/form/RygenMultiSelect.vue';
import PopupInputgroup from '@rygen/rygen-ui/components/popup/PopupInputgroup.vue';
import KeyValuePair from '@rygen/rygen-ui/components/ui/KeyValuePair.vue';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';

const props = defineProps({
  id: {
    type: String,
    default: () => useUid('popup-ref-num-value-edit').uid,
  },
  modelValue: {
    type: ReferenceNumber,
    default: () => new ReferenceNumber(),
  },
  clientTypes: {
    type: Array as PropType<ClientReferenceNumberType[]>,
    default: () => [],
  },
  placeholder: {
    type: String,
    default: () => undefined,
  },
  validationRules: {
    type: Object as PropType<Record<string, any>>,
    default: () => undefined,
  },
  appendTo: {
    type: String as PropType<'body' | 'self'>,
    default: () => 'self',
  },
  disabled: {
    type: [Boolean, String, Number, Array, Object],
    default: () => false,
  },
  editable: {
    type: Boolean,
    default: () => false,
  },
});
const {
  id,
  modelValue,
  clientTypes,
  placeholder,
  validationRules,
  appendTo,
  disabled,
  editable,
} = toRefs(props);
const emit = defineEmits<{
  (event: 'delete', refNum: ReferenceNumber): void;
  (event: 'update:model-value', newVal: ReferenceNumber): void;
}>();

defineSlots<{
  placeholder: (props: { label: string; placeholder?: string }) => any;
}>();

const { d, t } = useI18n();

const internalValue: Ref<ReferenceNumber> = ref(
  new ReferenceNumber(modelValue.value),
);
const wrappedValue = ref({ internalValue });
const rules = {
  internalValue: validationRules?.value ?? {},
};
const { validate, v$ } = useValidations(wrappedValue, rules, id.value);

const popup: Ref<ComponentPublicInstance | null> = ref(null);
const input: Ref<ComponentPublicInstance | null> = ref(null);

const label = computed(() => internalValue.value.typeName);

// TODO: Move this, `useReferenceNumberValueTypes, and the template into a new
// component. It's used here, in `NewReferenceNumberEdit`, and in `ReferenceNumberEdit`
const clientReferenceNumberType = computed(() => {
  return clientTypes.value.find(
    (clientType) =>
      clientType.referenceNumberType?.naturalId &&
      clientType.referenceNumberType.naturalId ===
        internalValue.value?.typeNaturalId,
  );
});

const valueOptionByValue = computed(() => {
  return new Map(
    clientReferenceNumberType.value?.referenceNumberType?.valueOptions?.map(
      (option) => [option.value, option],
    ),
  );
});

const {
  isDateOrTimeType,
  isMultiChoice,
  isFreeformMultiChoice,
  isTimeType,
  isTimeTypeOnly,
} = useReferenceNumberValueTypes(clientReferenceNumberType);

const placeholderValue = computed(() => {
  if (!modelValue.value.value) {
    return placeholder?.value ?? '-';
  } else if (
    modelValue.value.valueAsDate &&
    clientReferenceNumberType.value?.referenceNumberType?.valueType ===
      ReferenceNumberValueType.DATE
  ) {
    return d(modelValue.value.valueAsDate, 'numeric');
  } else if (
    modelValue.value.valueAsDate &&
    clientReferenceNumberType.value?.referenceNumberType?.valueType ===
      ReferenceNumberValueType.DATETIME
  ) {
    return d(modelValue.value.valueAsDate, 'numericDateTime');
  } else if (
    modelValue.value.valueAsDate &&
    clientReferenceNumberType.value?.referenceNumberType?.valueType ===
      ReferenceNumberValueType.TIME
  ) {
    return d(modelValue.value.valueAsDate, 'numericTime');
  } else {
    const currentValue = modelValue.value.value;
    return valueOptionByValue.value.get(currentValue)?.label ?? currentValue;
  }
});

const onFocus = () => {
  if (disabled.value) {
    return;
  }

  nextTick(() => input.value?.$el?.focus());
};

const onSave = async () => {
  let isValid = validationRules?.value ? await validate() : true;
  if (!isValid) {
    return;
  }

  internalValue.value.value = internalValue.value?.value?.replace(
    /[^\0-\x7F]/g,
    '',
  );

  if (internalValue.value !== modelValue.value) {
    emit('update:model-value', internalValue.value);
  }

  closePopup();
};

// Handles the click-outside / blur-triggered save from PopupInputgroup's overlay.
// Unlike onSave, reverts internalValue on invalid input because the popup is
// already closed by the library before this fires — the user has no way to correct.
const onAutoSave = async () => {
  const isValid = validationRules?.value ? await validate() : true;
  if (!isValid) {
    internalValue.value = new ReferenceNumber(modelValue.value);
    v$.value.$reset();
    return;
  }

  await onSave();
};

const onCancel = () => {
  internalValue.value = new ReferenceNumber(modelValue.value);
};

const closePopup = () => {
  if (!popup.value) {
    return;
  }

  const popupRef = popup.value as unknown as typeof PopupInputgroup;
  popupRef.onCancel();
};

const onDelete = () => {
  if (!authorization.hasAuthority('referenceData', 'delete')) {
    return;
  }

  emit('delete', internalValue.value);
};

watch(
  modelValue,
  (newVal) => {
    internalValue.value = new ReferenceNumber(newVal);
    v$.value.$reset();
  },
  { immediate: true },
);
</script>

<style>
.popup-ref-num-value-edit.popup-inputgroup {
  --popup-ref-num-label-width: 50%;
  --popup-ref-num-value-width: var(--popup-ref-num-label-width);

  & > .p-card {
    top: calc(var(--sp-6) * -1);
    /*
    make text in popup card line up with text in placeholder
    width of label minus popup card padding and input padding
    */
    left: calc(
      var(--popup-ref-num-label-width) - var(--sp-4) -
        var(--p-inputtext-padding-x)
    );
    width: max-content;
  }

  .key-value-pair.key-value-pair-inline {
    .key-value-pair-label {
      width: 50%;
      width: var(--popup-ref-num-label-width);
    }

    .key-value-pair-value {
      text-align: start;
      width: var(--popup-ref-num-value-width);
    }
  }
}
</style>
